<!-- views/TrainingTask.vue -->
<!-- Phase 2: 個別タスク練習ページ -->

<template>
  <div class="training-task-page">
    <!-- ヘッダー -->
    <div class="page-header">
      <div class="header-content">
        <div class="header-info">
          <div class="header-primary">練習モード</div>
          <div class="header-secondary">
            <span v-if="dayId !== null" class="header-secondary-item">
              Day {{ dayId }}
            </span>
            <span v-if="totalTasks > 0" class="header-secondary-item">
              {{ currentIndex + 1 }} / {{ totalTasks }}
            </span>
          </div>
        </div>
        <button @click="goBack" class="back-button">← 戻る</button>
      </div>
    </div>

    <!-- ローディング -->
    <div v-if="isLoading" class="loading-container">
      <div class="spinner"></div>
      <p>タスクを読み込み中...</p>
    </div>

    <!-- エラー表示 -->
    <div v-else-if="error" class="error-container">
      <h3>❌ エラーが発生しました</h3>
      <p>{{ error }}</p>
      <button @click="loadTask" class="retry-button">再試行</button>
    </div>

    <!-- 練習カード（ProsodyファーストUI） -->
    <div v-else-if="task" class="training-content">
      <!-- スコアサマリー -->
      <div class="score-summary">
        <div class="score-row top-row">
          <div
            v-for="(label, idx) in topLabels"
            :key="label"
            class="score-chip"
            :class="[{ jump: highlightTop[idx] }, `tier-${idx}`]"
          >
            <div class="score-chip-label">{{ label }}</div>
            <div class="score-chip-value">{{ fmtScore(topScoreSlots[idx]) }}</div>
          </div>
        </div>

        <div class="score-row current-row">
          <div class="score-current" :class="{ jump: highlightCurrent }">
            <span class="score-label">今回スコア</span>
            <span class="score-value">{{ fmtScore(currentScoreDisplay) }}</span>
          </div>
        </div>

        <div class="score-row meta-row">
          <div class="score-meta" :class="{ jump: highlightTotal }">
            <div class="score-meta-label">今回合計</div>
            <div class="score-meta-value">{{ fmtScore(totalScoreDisplay) }}</div>
          </div>
          <div class="score-meta" :class="{ jump: highlightBest }">
            <div class="score-meta-label">ベスト</div>
            <div class="score-meta-value">{{ fmtScore(bestTotalScore) }}</div>
          </div>
          <div class="score-meta" :class="{ jump: highlightRank }">
            <div class="score-meta-label">ランク</div>
            <div class="score-meta-value">
              <span class="rank-emoji" v-if="rankEmoji">{{ rankEmoji }}</span>
              {{ rankLabel }}
            </div>
          </div>
        </div>

        <transition name="hi-score">
          <div class="hi-score-banner" v-if="showHiScoreBanner">
            🎉 HI-SCORE 更新！ +{{ hiScoreDelta }} UP！
          </div>
        </transition>
      </div>

      <!-- TTS 速度コントロール -->
      <div class="tts-controls" v-if="task?.audio_ref">
        <span class="tts-label">模範音</span>
        <button class="tts-btn" @click="playTTS(0.6)">×0.6</button>
        <button class="tts-btn" @click="playTTS(0.8)">×0.8</button>
        <button class="tts-btn" @click="playTTS(1.0)">×1.0</button>
      </div>

      <ProsodyPracticeCard 
        v-if="task && dayId !== null"
        :task="task"
        :session-stats="sessionStats"
        :total-stats="totalStats"
        @complete="onTrainingComplete"
      />
      <div v-else class="loading-container">
        <div class="spinner"></div>
        <p>タスク情報を準備中...</p>
      </div>
      
      <!-- ===== 本番モード（チャレンジ） ===== -->
      <div class="challenge-card" v-if="SHOW_PRODUCTION_MODE && task">
        <div class="challenge-header">
          <h3>本番モード</h3>
          <div class="challenge-actions">
            <button
              @click="startProductionLoop"
              :disabled="productionConnecting || productionRunning"
              class="challenge-button start"
              :class="{
                connecting: productionConnecting,
                active: productionRunning
              }"
            >
              <template v-if="productionConnecting">接続中…</template>
              <template v-else-if="productionRunning">録音中</template>
              <template v-else>▶ 本番開始</template>
            </button>
            <button
              @click="stopProductionLoop"
              :disabled="!productionRunning && !productionConnecting"
              class="challenge-button stop"
            >
              ■ 停止
            </button>
          </div>
        </div>
        <div class="challenge-status" v-if="productionRunning || productionAttempts > 0">
          <div class="challenge-stat">
            <span class="stat-label">試行回数:</span>
            <span class="stat-value">{{ productionAttempts }}</span>
          </div>
          <div class="challenge-stat">
            <span class="stat-label">今回スコア:</span>
            <span class="stat-value" :class="getScoreClass(productionScore)">{{ fmtScore(productionScore) }}</span>
          </div>
          <div class="challenge-stat">
            <span class="stat-label">ベスト:</span>
            <span class="stat-value" :class="getScoreClass(productionBest)">{{ fmtScore(productionBest) }}</span>
          </div>
          <div class="challenge-stat" v-if="productionBestMedal">
            <span class="stat-label">バッジ:</span>
            <span class="stat-value badge" :class="`badge-${productionBestMedal}`">
              {{ productionBestMedal === 'gold' ? '🥇 金' : productionBestMedal === 'silver' ? '🥈 銀' : '🥉 銅' }}
            </span>
          </div>
        </div>
        <div class="challenge-clip" v-if="productionClipUrl">
          <audio :src="productionClipUrl" controls></audio>
        </div>
        <div class="challenge-share" v-if="productionCanShare">
          <button @click="shareProductionClip" class="share-button">
            ✨ シェア（95+）
          </button>
        </div>
      </div>
      
      <!-- ナビゲーション（ProsodyPracticeCardには含まれないため追加） -->
      <div class="task-navigation" v-if="task && dayId !== null">
        <button 
          @click="previousTask" 
          :disabled="!canGoPrevious"
          class="nav-button prev"
        >
          ← 前へ
        </button>
        
        <div class="progress-info">
          {{ currentIndex + 1 }} / {{ totalTasks }}
        </div>
        
        <button 
          @click="nextTask" 
          :disabled="!canGoNext"
          class="nav-button next"
        >
          次へ →
        </button>
      </div>
    </div>

    <!-- 完了メッセージ -->
    <div v-if="showCompletionMessage" class="completion-overlay">
      <div class="completion-modal">
        <div class="completion-icon">🎉</div>
        <h2>おめでとうございます！</h2>
        <p>{{ task.sentence }}</p>
        <div class="completion-stats">
          <div class="stat-item">
            <span class="stat-label">最終スコア</span>
            <span class="stat-value" :class="getScoreClass(finalScore)">
              {{ finalScore }}
            </span>
          </div>
          <div class="stat-item">
            <span class="stat-label">セッション試行</span>
            <span class="stat-value">{{ sessionStats.attempts }}</span>
          </div>
          <div class="stat-item">
            <span class="stat-label">セッション最高</span>
            <span class="stat-value" :class="getScoreClass(sessionStats.best)">
              {{ sessionStats.best }}
            </span>
          </div>
        </div>
        
        <div class="completion-actions">
          <button @click="retryTask" class="action-button retry">
            🔄 再挑戦
          </button>
          <button @click="nextTask" class="action-button next" v-if="canGoNext">
            次の文 →
          </button>
          <button @click="goBackToDay" class="action-button back">
            Day一覧に戻る
          </button>
          <button v-if="canPost" @click="openPostModal" class="action-button next">
            ✅ 98点達成を投稿候補にする
          </button>
        </div>
      </div>
    </div>

    <!-- 投稿候補モーダル -->
    <div v-if="showPostModal" class="completion-overlay">
      <div class="completion-modal">
        <h2>投稿候補</h2>
        <p>この練習（{{ finalScore }}点）をコミュニティに共有しますか？（任意）</p>
        <textarea v-model="postCaption" rows="3" placeholder="ひとこと（任意）"></textarea>
        <div class="completion-actions">
          <button @click="showPostModal=false" class="action-button back">キャンセル</button>
          <button :disabled="posting || postDone" @click="createPost" class="action-button next">
            {{ posting ? '投稿中...' : (postDone ? '投稿済み' : '投稿する') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, onBeforeUnmount, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import ProsodyPracticeCard from '../components/ProsodyPracticeCard.vue'
import { supabase } from '../supabaseClient.js'
import { AzureSpeechEvaluator, loadSpeechSDK, applyPronAssessment } from '../utils/azureSpeechEvaluator'

const router = useRouter()
const route = useRoute()

// 本番モードの表示フラグ（要望によりデフォルト非表示）
const SHOW_PRODUCTION_MODE = false

// 1) 依存される ref/computed を先に宣言
const isLoading = ref(true)
const error = ref(null)
const task = ref(null)
const tasks = ref([])
const currentIndex = ref(0)
const sessionStats = ref({ attempts: 0, best: 0 })
const lastScores = ref({ overall: null, accuracy: null, fluency: null, completeness: null, prosody: null })
const totalStats = ref({ attempts_total: 0, best_overall: 0 })
const showCompletionMessage = ref(false)
const finalScore = ref(0)
const showPostModal = ref(false)
const posting = ref(false)
const postDone = ref(false)
const postCaption = ref('')

// 2) 計算プロパティ（安全な参照順）
const taskId = computed(() => route.params.taskId)
const dayId = computed(() => {
  const n = Number(route.query.dayId ?? task.value?.day_number)
  return Number.isFinite(n) && n > 0 ? n : null
})
const taskIndex = computed(() => parseInt(route.query.index) || 0)

// modeとlevelをクエリパラメータまたはtaskオブジェクトから取得
const mode = computed(() => {
  return route.query.mode || task.value?.mode || 'otoeigo'
})
const level = computed(() => {
  return route.query.level || task.value?.level || null
})

const totalTasks = computed(() => tasks.value.length)
const canGoNext = computed(() => currentIndex.value < totalTasks.value - 1)
const canGoPrevious = computed(() => currentIndex.value > 0)
const canPost = computed(() => finalScore.value >= 98)

// スコア表示・ランキング関連
const TH_BRONZE = computed(() => task.value?.badge_bronze ?? 85)
const TH_SILVER = computed(() => task.value?.badge_silver ?? 90)
const TH_GOLD = computed(() => task.value?.badge_gold ?? 95)
const ROLLING_WINDOW_MS = 30000
const topLabels = ['🥇 1st', '🥈 2nd', '🥉 3rd']
const rollingScores = ref([])
const topScoreSlots = ref([null, null, null])
const currentScoreDisplay = ref(null)
const totalScoreDisplay = ref(0)
const bestTotalScore = ref(0)
const bestRank = ref(null)
const showHiScoreBanner = ref(false)
const hiScoreDelta = ref(0)

const highlightTop = ref([false, false, false])
const highlightCurrent = ref(false)
const highlightTotal = ref(false)
const highlightBest = ref(false)
const highlightRank = ref(false)

const rankLabelMap = { gold: '金', silver: '銀', bronze: '銅' }
const rankEmojiMap = { gold: '🥇', silver: '🥈', bronze: '🥉' }

let hiScoreTimer = null
const highlightTopTimers = [null, null, null]
const highlightFlagTimers = {
  current: null,
  total: null,
  best: null,
  rank: null
}

const rankLabel = computed(() => rankLabelMap[bestRank.value] ?? '---')
const rankEmoji = computed(() => rankEmojiMap[bestRank.value] ?? '')

function resetScoreTracking() {
  rollingScores.value = []
  topScoreSlots.value = [null, null, null]
  currentScoreDisplay.value = null
  totalScoreDisplay.value = 0
  bestTotalScore.value = 0
  bestRank.value = null
  hiScoreDelta.value = 0
  showHiScoreBanner.value = false
  highlightTop.value = [false, false, false]
  highlightCurrent.value = false
  highlightTotal.value = false
  highlightBest.value = false
  highlightRank.value = false
  if (hiScoreTimer) {
    clearTimeout(hiScoreTimer)
    hiScoreTimer = null
  }
  highlightTopTimers.forEach((timer, idx) => {
    if (timer) {
      clearTimeout(timer)
      highlightTopTimers[idx] = null
    }
  })
  Object.keys(highlightFlagTimers).forEach((key) => {
    const timer = highlightFlagTimers[key]
    if (timer) {
      clearTimeout(timer)
      highlightFlagTimers[key] = null
    }
  })
}

function triggerTopHighlight(index) {
  if (index < 0 || index > 2) return
  const resetArr = [...highlightTop.value]
  resetArr[index] = false
  highlightTop.value = resetArr
  requestAnimationFrame(() => {
    const arr = [...highlightTop.value]
    arr[index] = true
    highlightTop.value = arr
    if (highlightTopTimers[index]) {
      clearTimeout(highlightTopTimers[index])
    }
    highlightTopTimers[index] = setTimeout(() => {
      const next = [...highlightTop.value]
      next[index] = false
      highlightTop.value = next
      highlightTopTimers[index] = null
    }, 450)
  })
}

function triggerHighlightFlag(flagRef, key) {
  if (!flagRef) return
  flagRef.value = false
  requestAnimationFrame(() => {
    flagRef.value = true
    if (highlightFlagTimers[key]) {
      clearTimeout(highlightFlagTimers[key])
    }
    highlightFlagTimers[key] = setTimeout(() => {
      flagRef.value = false
      highlightFlagTimers[key] = null
    }, 450)
  })
}

function updateBestRankLabel(slotCount = 3) {
  if (!bestTotalScore.value || bestTotalScore.value <= 0) {
    bestRank.value = null
    return
  }
  const divisor = Math.max(slotCount, 1)
  const normalized = Math.round(bestTotalScore.value / divisor)
  bestRank.value = determineProductionMedal(normalized) || null
}

function getActiveSlotCount(scores) {
  return scores.filter(value => Number.isFinite(value)).length || 0
}

function recordRollingScore(score) {
  if (!Number.isFinite(score)) {
    return { total: totalScoreDisplay.value, activeCount: getActiveSlotCount(topScoreSlots.value) }
  }
  const now = Date.now()
  rollingScores.value = rollingScores.value
    .filter(entry => now - entry.at <= ROLLING_WINDOW_MS)
    .concat({ score, at: now })

  const sorted = [...rollingScores.value].sort((a, b) => b.score - a.score)
  const newTop = Array.from({ length: 3 }, (_, idx) => sorted[idx]?.score ?? null)
  return applyTopScores(newTop)
}

function applyTopScores(newScores) {
  const normalized = newScores.slice(0, 3)
  while (normalized.length < 3) normalized.push(null)
  const prev = topScoreSlots.value
  normalized.forEach((score, idx) => {
    if (prev[idx] !== score && score !== null) {
      triggerTopHighlight(idx)
    }
  })
  topScoreSlots.value = normalized
  const total = updateTotalScoreValue(normalized)
  const activeCount = getActiveSlotCount(normalized)
  return { total, activeCount }
}

function sumScores(scores) {
  return scores.reduce((sum, value) => {
    return sum + (Number.isFinite(value) ? Math.round(value) : 0)
  }, 0)
}

function updateTotalScoreValue(scores) {
  const total = sumScores(scores)
  if (totalScoreDisplay.value !== total) {
    totalScoreDisplay.value = total
    triggerHighlightFlag(highlightTotal, 'total')
  }
  return total
}

function updateCurrentScoreDisplay(score) {
  if (!Number.isFinite(score)) return
  currentScoreDisplay.value = score
  triggerHighlightFlag(highlightCurrent, 'current')
}

function handleHiScore(totalScore, slotCount = 3) {
  const roundedTotal = Math.round(totalScore)
  const previousBest = bestTotalScore.value || 0
  const improved = roundedTotal > previousBest
  const divisor = Math.max(slotCount, 1)

  if (improved) {
    hiScoreDelta.value = roundedTotal - previousBest
    bestTotalScore.value = roundedTotal
    updateBestRankLabel(slotCount)
    triggerHighlightFlag(highlightBest, 'best')
    triggerHighlightFlag(highlightRank, 'rank')
    showHiScoreBanner.value = true
    if (hiScoreTimer) clearTimeout(hiScoreTimer)
    hiScoreTimer = setTimeout(() => {
      showHiScoreBanner.value = false
      hiScoreTimer = null
    }, 3200)
  }

  persistScoreStats(roundedTotal)
  const attempts = (totalStats.value?.attempts_total || 0) + 1
  const best = Math.max(totalStats.value?.best_overall || 0, roundedTotal)
  totalStats.value = {
    attempts_total: attempts,
    best_overall: best
  }
  if (!improved && bestTotalScore.value < best) {
    bestTotalScore.value = best
    updateBestRankLabel()
  }
}

async function persistScoreStats(score) {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user || !task.value) return
    const { error } = await supabase.rpc('increment_attempt_and_update_best', {
      p_user_id: user.id,
      p_task_id: task.value.id,
      p_score: score
    })
    if (error) {
      console.warn('📉 統計更新エラー:', error)
    }
  } catch (error) {
    console.warn('📉 統計保存例外:', error)
  }
}

// 3) dayId/task を使う関数は後ろで定義
const loadTask = async () => {
  try {
    isLoading.value = true
    error.value = null
    resetScoreTracking()
    
    await stopProductionLoop()
    resetProductionState()
    await disposeProductionSession()
    
    // 特定のタスクを取得
    const { data: taskData, error: taskError } = await supabase
      .from('training_tasks')
      .select('*')
      .eq('id', taskId.value)
      .single()
    
    if (taskError) throw taskError
    
    task.value = taskData
    
    // Day内の全タスクを取得（ナビゲーション用）
    if (dayId.value) {
      await loadDayTasks()
    } else {
      // Day情報がない場合は単一タスクとして扱う
      tasks.value = [taskData]
    }
    
    // 統計情報を取得
    await loadTaskStats()
    
    console.log('✅ タスク読み込み完了:', {
      taskId: taskId.value,
      dayId: dayId.value,
      taskIndex: taskIndex.value
    })
    
  } catch (err) {
    console.error('タスク読み込み失敗:', err)
    error.value = err.message || 'タスクの読み込みに失敗しました'
  } finally {
    isLoading.value = false
  }
}

const loadDayTasks = async () => {
  try {
    if (!dayId.value) {
      console.warn('dayIdが設定されていません')
      return
    }
    
    // Day内の全タスクを取得（day_number, mode, levelで絞り込み）
    let q = supabase
      .from('training_tasks')
      .select('*')
      .eq('day_number', dayId.value)
      .eq('mode', mode.value)  // modeでフィルタリング
      .order('task_order', { ascending: true })
    
    // levelがあるモード（英検、ESAT-J）の場合はlevelでもフィルタリング
    if (level.value) {
      q = q.eq('level', level.value)
    }
    
    const { data: tasksData, error: tasksError } = await q
    
    if (tasksError) throw tasksError
    
    tasks.value = tasksData || []
    
    // 現在のタスクがリストにない場合は追加（念のため）
    if (task.value && !tasks.value.find(t => t.id === task.value.id)) {
      tasks.value.push(task.value)
      // task_orderでソート（追加したタスクが正しい位置に来るように）
      tasks.value.sort((a, b) => (a.task_order || 0) - (b.task_order || 0))
    }
    
    // currentIndexを更新（taskIndexを優先、なければ現在のタスクの位置から）
    if (task.value) {
      // URLのtaskIndexが有効な場合はそれを使用（ナビゲーション後の正しいインデックス）
      if (taskIndex.value >= 0 && taskIndex.value < tasks.value.length) {
        currentIndex.value = taskIndex.value
      } else {
        // フォールバック: 現在のタスクの位置から取得
        const foundIndex = tasks.value.findIndex(t => t.id === task.value.id)
        if (foundIndex >= 0) {
          currentIndex.value = foundIndex
        } else {
          currentIndex.value = 0
        }
      }
    } else {
      currentIndex.value = taskIndex.value >= 0 ? taskIndex.value : 0
    }
    
    console.log('✅ Dayタスク読み込み完了:', {
      dayId: dayId.value,
      mode: mode.value,
      level: level.value,
      taskCount: tasks.value.length,
      currentIndex: currentIndex.value
    })
    
  } catch (error) {
    console.error('Dayタスク読み込み失敗:', error)
  }
}

const loadTaskStats = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user || !task.value) return
    
    // タスクの統計を取得
    const { data, error } = await supabase.rpc('get_total_stats', {
      p_user_id: user.id,
      p_task_id: task.value.id
    })
    
    if (!error && data && data.length > 0) {
      totalStats.value = data[0]
    } else {
      totalStats.value = { attempts_total: 0, best_overall: 0 }
    }
    
    bestTotalScore.value = totalStats.value.best_overall ?? 0
    updateBestRankLabel()
    
  } catch (error) {
    console.error('タスク統計読み込み失敗:', error)
  }
}

const nextTask = async () => {
  if (canGoNext.value) {
    const nextIndex = currentIndex.value + 1
    const nextTask = tasks.value[nextIndex]
    if (nextTask) {
      // URLを先に更新（modeとlevelも含める）
      const query = {
        dayId: dayId.value,
        index: nextIndex,
        mode: mode.value
      }
      if (level.value) {
        query.level = level.value
      }
      
      await router.replace({
        name: 'TrainingTask',
        params: { taskId: String(nextTask.id) },
        query
      })
      
      // URL更新後にloadTask()を呼び出す
      await loadTask()
    }
  }
}

const previousTask = async () => {
  if (currentIndex.value > 0) {
    const prevIndex = currentIndex.value - 1
    const prevTask = tasks.value[prevIndex]
    if (prevTask) {
      // URLを先に更新（modeとlevelも含める）
      const query = {
        dayId: dayId.value,
        index: prevIndex,
        mode: mode.value
      }
      if (level.value) {
        query.level = level.value
      }
      
      await router.replace({
        name: 'TrainingTask',
        params: { taskId: String(prevTask.id) },
        query
      })
      
      // URL更新後にloadTask()を呼び出す
      await loadTask()
    }
  }
}

const onTrainingComplete = async (payload) => {
  console.log('📊 練習完了イベント受領:', payload)
  
  // セッション統計を更新
  sessionStats.value.attempts++
  // ペイロードがオブジェクトなら各スコアを取り出し、数値ならoverallとして扱う
  let overallScore = 0
  if (payload && typeof payload === 'object') {
    const { overall, accuracy, fluency, completeness, prosody } = payload
    lastScores.value = {
      overall: toNum(overall),
      accuracy: toNum(accuracy),
      fluency: toNum(fluency),
      completeness: toNum(completeness),
      prosody: toNum(prosody)
    }
    overallScore = lastScores.value.overall ?? 0
    console.log('✅ スコア更新完了:', lastScores.value)
  } else {
    overallScore = Number(payload) || 0
    lastScores.value.overall = overallScore
    console.log('✅ スコア更新完了（overallのみ）:', lastScores.value)
  }
  sessionStats.value.best = Math.max(sessionStats.value.best, overallScore)
  
  finalScore.value = overallScore
  updateCurrentScoreDisplay(overallScore)
  const { total: latestTotal, activeCount } = recordRollingScore(overallScore)
  handleHiScore(latestTotal, activeCount)
  
  // 評価結果の保存は本番録音モードのみで実行するため停止（練習モードは接続のみ）
  /*
  try {
    const { data: { user } } = await supabase.auth.getUser()
    if (user && task.value) {
      const { data: existing } = await supabase
        .from('evaluations')
        .select('id, first_score')
        .eq('user_id', user.id)
        .eq('lesson_id', task.value.id)
        .single()

      const upsertData = {
        user_id: user.id,
        lesson_id: task.value.id,
        overall: overallScore,
        best_score: overallScore,
        evaluation_count: 1,
        updated_at: new Date().toISOString()
      }

      if (!existing) {
        upsertData.first_score = overallScore
      }

      const { data, error } = await supabase
        .from('evaluations')
        .upsert(upsertData, { onConflict: 'user_id,lesson_id' })
      if (error) console.warn('評価保存エラー:', error)
    }
  } catch (e) {
    console.warn('評価保存例外:', e)
  }
  */
  
  // 完了メッセージを表示（連続練習の妨げになるため無効化）
  // showCompletionMessage.value = true
  
  console.log('練習完了:', { 
    score: overallScore, 
    sessionStats: sessionStats.value,
    totalStats: totalStats.value,
    rollingScores: rollingScores.value
  })
}

const retryTask = () => {
  showCompletionMessage.value = false
  // セッション統計をリセット
  sessionStats.value = { attempts: 0, best: 0 }
}

const openPostModal = () => {
  showPostModal.value = true
}

const createPost = async () => {
  if (posting.value || postDone.value) return
  posting.value = true
  try {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user || !task.value) throw new Error('未ログインまたはタスク不明')
    // 直近のevaluation取得（user+lesson）
    const { data: ev, error: evErr } = await supabase
      .from('evaluations')
      .select('id, audio_url, overall')
      .eq('user_id', user.id)
      .eq('lesson_id', task.value.id)
      .order('updated_at', { ascending: false })
      .limit(1)
    if (evErr) throw evErr
    const evaluation = ev && ev[0]
    if (!evaluation) throw new Error('評価レコードが見つかりません')
    // posts 作成
    const { error: postErr } = await supabase
      .from('posts')
      .insert({
        user_id: user.id,
        evaluation_id: evaluation.id,
        caption: postCaption.value || null
      })
    if (postErr) throw postErr
    postDone.value = true
  } catch (e) {
    console.error('投稿作成エラー:', e)
    alert('投稿に失敗しました。後でもう一度お試しください。')
  } finally {
    posting.value = false
  }
}

const goBack = () => {
  const query = {}
  if (dayId.value) {
    query.day = String(dayId.value)
  }
  if (mode.value) {
    query.mode = String(mode.value)
  }
  if (level.value) {
    query.level = String(level.value)
  }
  if (task.value?.id) {
    query.taskId = task.value.id
  }
  if (Object.keys(query).length > 0) {
    router.push({ path: '/training', query })
  } else {
    router.push('/training')
  }
}

const goBackToDay = () => {
  showCompletionMessage.value = false
  goBack()
}

const playTTS = (rate = 1.0) => {
  try {
    if (!task.value?.audio_ref) return
    const audio = new Audio(task.value.audio_ref)
    audio.playbackRate = rate
    audio.play()
  } catch (e) {
    console.warn('TTS再生エラー:', e)
  }
}

const getScoreClass = (score) => {
  if (score >= 90) return 'excellent'
  if (score >= 70) return 'good'
  return 'needs-improvement'
}

const toNum = (v) => {
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

const fmtScore = (v) => {
  return v === null || v === undefined ? '-' : Math.round(v)
}

// ========= 本番モード（Production）関連 =========

const speechEvaluator = new AzureSpeechEvaluator()

const PRODUCTION_SILENCE_TIMEOUT = 5000
const PRODUCTION_DURATION_LIMIT = 30000
const PRODUCTION_INTERVAL_MS = 2000

const productionRunning = ref(false)
const productionConnecting = ref(false)
const productionAttempts = ref(0)
const productionScore = ref(null)
const productionBest = ref(null)
const productionBestMedal = ref(null)
const productionCurrentMedal = ref(null)
const productionClipUrl = ref(null)
const productionStartAt = ref(0)

const productionCanShare = computed(() => (productionScore.value ?? 0) >= 95 && !!productionClipUrl.value)
const refText = computed(() => task.value?.sentence ?? task.value?.prompt_text ?? '')

const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms))

class ProductionSession {
  constructor(evaluator, referenceTextProvider, onReady) {
    this.evaluator = evaluator
    this.referenceTextProvider = referenceTextProvider
    this.onReady = onReady
    this.mediaStream = null
    this.audioContext = null
    this.workletNode = null
    this.muteGain = null
    this.sourceNode = null
    this.pushStream = null
    this.recognizer = null
    this.connection = null
    this.speechConfig = null
    this.audioConfig = null
    this.speechsdk = null
    this.isRecording = false
    this.recognitionActive = false
    this.ready = false
    this.disposed = false
    this._activeReject = null
  }

  async prepare() {
    if (this.ready && !this.disposed) {
      return this.mediaStream
    }

    const AudioContextClass = window.AudioContext || window.webkitAudioContext
    if (!AudioContextClass) {
      const err = new Error('このブラウザは AudioContext をサポートしていません')
      err.code = 'MICROPHONE_NOT_SUPPORTED'
      err.errorType = 'MICROPHONE_ERROR'
      throw err
    }

    this.mediaStream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: false }
    })

    this.audioContext = new AudioContextClass({ sampleRate: 48000 })
    await this.audioContext.audioWorklet.addModule('/pcm16k-worklet.js')

    this.workletNode = new AudioWorkletNode(this.audioContext, 'pcm16k-processor')
    this.muteGain = this.audioContext.createGain()
    this.muteGain.gain.value = 0
    this.workletNode.port.onmessage = (event) => {
      if (!this.pushStream || !this.isRecording) return
      const payload = event.data || {}
      if (payload.type !== 'pcm' || !payload.data) return
      this.pushStream.write(new Uint8Array(payload.data))
    }

    this.sourceNode = this.audioContext.createMediaStreamSource(this.mediaStream)
    this.sourceNode.connect(this.workletNode)
    this.workletNode.connect(this.muteGain)
    this.muteGain.connect(this.audioContext.destination)

    this.speechsdk = await loadSpeechSDK()
    const { token, region } = await this.evaluator.ensureValidToken()
    this.speechConfig = this.speechsdk.SpeechConfig.fromAuthorizationToken(token, region)
    this.speechConfig.speechRecognitionLanguage = 'en-US'
    this.speechConfig.outputFormat = this.speechsdk.OutputFormat.Detailed
    this.speechConfig.setProperty(this.speechsdk.PropertyId.SpeechServiceConnection_InitialSilenceTimeoutMs, '5000')
    this.speechConfig.setProperty(this.speechsdk.PropertyId.SpeechServiceConnection_EndSilenceTimeoutMs, '2000')

    const format = this.speechsdk.AudioStreamFormat.getWaveFormatPCM(16000, 16, 1)
    this.pushStream = this.speechsdk.AudioInputStream.createPushStream(format)
    this.audioConfig = this.speechsdk.AudioConfig.fromStreamInput(this.pushStream)
    this.recognizer = new this.speechsdk.SpeechRecognizer(this.speechConfig, this.audioConfig)
    await applyPronAssessment(this.recognizer, this.referenceTextProvider(), {
      granularity: 'Phoneme',
      enableProsody: false
    })
    if (typeof this.onReady === 'function') {
      try {
        this.onReady()
      } catch (err) {
        console.warn('⚠️ Production ready callback エラー:', err)
      }
    }

    if (this.speechsdk.Connection && typeof this.speechsdk.Connection.fromRecognizer === 'function') {
      try {
        this.connection = this.speechsdk.Connection.fromRecognizer(this.recognizer)
        this.connection?.openConnection?.()
      } catch (err) {
        console.warn('⚠️ Production preconnect 失敗:', err)
      }
    }

    this.ready = true
    this.disposed = false
    return this.mediaStream
  }

  getStream() {
    return this.mediaStream
  }

  async recognizeOnce({ silenceMs = PRODUCTION_SILENCE_TIMEOUT } = {}) {
    if (!this.recognizer) {
      throw new Error('認識器が初期化されていません')
    }

    const text = this.referenceTextProvider?.()
    if (text) {
      await applyPronAssessment(this.recognizer, text, {
        granularity: 'Phoneme',
        enableProsody: false
      })
    }

    const speechsdk = this.speechsdk
    return new Promise((resolve, reject) => {
      let finished = false
      let silenceTimer = null

      const finalize = async (payload, isError = false) => {
        if (finished) return
        finished = true
        if (silenceTimer) {
          clearTimeout(silenceTimer)
          silenceTimer = null
        }
        try {
          await this.stopRecognition()
        } catch (err) {
          console.warn('⚠️ stopRecognition エラー:', err)
        }
        this.recognizer.recognizing = () => {}
        this.recognizer.recognized = () => {}
        this.recognizer.canceled = () => {}
        this._activeReject = null
        this.isRecording = false
        if (isError) reject(payload)
        else resolve(payload)
      }

      this.recognizer.recognizing = (_sender, e) => {
        const partial = e.result?.text?.trim()
        if (partial && partial.replace(/[^a-zA-Z]/g, '').length >= 2 && silenceTimer) {
          clearTimeout(silenceTimer)
          silenceTimer = null
        }
      }

      this.recognizer.recognized = async (_sender, e) => {
        const result = e.result
        if (!result || result.reason === speechsdk.ResultReason.NoMatch) {
          await finalize({ status: 'silence', result })
    return
        }
        if (result.reason === speechsdk.ResultReason.Canceled) {
          const err = new Error(result.errorDetails || '認識がキャンセルされました')
          err.code = 'RECOGNITION_CANCELED'
          await finalize(err, true)
          return
        }
        await finalize({ status: 'success', result })
      }

      this.recognizer.canceled = async (_sender, e) => {
        const err = new Error(e.errorDetails || '認識がキャンセルされました')
        err.code = 'RECOGNITION_CANCELED'
        await finalize(err, true)
      }

      const startRecognition = () => new Promise((res, rej) => {
        this.recognizer.startContinuousRecognitionAsync(
          () => {
            this.recognitionActive = true
            this.isRecording = true
            if (silenceMs > 0) {
              silenceTimer = setTimeout(() => {
                finalize({ status: 'silence', reason: 'timeout' })
              }, silenceMs)
            }
            res()
          },
          (err) => {
            this.recognitionActive = false
            this.isRecording = false
            rej(err)
          }
        )
      })

      this._activeReject = async (error) => {
        await finalize(error ?? new Error('SESSION_STOPPED'), true)
      }

      if (this.audioContext?.state === 'suspended') {
        this.audioContext.resume().catch((err) => {
          console.warn('⚠️ audioContext.resume エラー:', err)
        })
      }

      startRecognition().catch(async (err) => {
        await finalize(err, true)
      })
    })
  }

  async stopRecognition() {
    if (!this.recognizer || !this.recognitionActive) {
      this.isRecording = false
      return
    }
    await new Promise((resolve, reject) => {
      this.recognizer.stopContinuousRecognitionAsync(
        () => {
          this.recognitionActive = false
          this.isRecording = false
          resolve()
        },
        (err) => {
          this.recognitionActive = false
          this.isRecording = false
          reject(err)
        }
      )
    })
  }

  cancelActiveAttempt() {
    if (this._activeReject) {
      const reject = this._activeReject
      this._activeReject = null
      reject(new Error('SESSION_STOPPED'))
    }
  }

  async dispose() {
    if (this.disposed) return
    this.disposed = true
    try {
      this.cancelActiveAttempt()
      await this.stopRecognition().catch(() => {})
    } catch {}

    if (this.recognizer) {
      try { this.recognizer.close() } catch {}
      this.recognizer = null
    }
    if (this.pushStream) {
      try { this.pushStream.close() } catch {}
      this.pushStream = null
    }
    if (this.connection) {
      try { this.connection.closeConnection?.() } catch {}
      this.connection = null
    }
    if (this.workletNode) {
      try { this.workletNode.port.onmessage = null } catch {}
      try { this.workletNode.disconnect() } catch {}
      this.workletNode = null
    }
    if (this.sourceNode) {
      try { this.sourceNode.disconnect() } catch {}
      this.sourceNode = null
    }
    if (this.muteGain) {
      try { this.muteGain.disconnect() } catch {}
      this.muteGain = null
    }
    if (this.audioContext) {
      try { await this.audioContext.close() } catch {}
      this.audioContext = null
    }
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach(track => track.stop())
      this.mediaStream = null
    }
    this.ready = false
  }
}

let productionSession = null
let productionLoopPromise = null

function determineProductionMedal(score) {
  if (score == null) return null
  if (score >= TH_GOLD.value) return 'gold'
  if (score >= TH_SILVER.value) return 'silver'
  if (score >= TH_BRONZE.value) return 'bronze'
  return null
}

function pickProductionMime() {
  if (MediaRecorder.isTypeSupported?.('audio/mp4')) return 'audio/mp4'
  if (MediaRecorder.isTypeSupported?.('audio/webm;codecs=opus')) return 'audio/webm;codecs=opus'
  return ''
}

function resetProductionState() {
  productionAttempts.value = 0
  productionScore.value = null
  productionBest.value = null
  productionBestMedal.value = null
  productionCurrentMedal.value = null
  productionStartAt.value = 0
  if (productionClipUrl.value) {
    URL.revokeObjectURL(productionClipUrl.value)
    productionClipUrl.value = null
  }
}

async function ensureProductionSession() {
  if (productionSession && !productionSession.disposed && productionSession.ready) {
    return productionSession
  }
  productionSession = new ProductionSession(
    speechEvaluator,
    () => refText.value,
    () => {
      productionConnecting.value = false
      productionRunning.value = true
    }
  )
  await productionSession.prepare()
  return productionSession
}

async function disposeProductionSession() {
  if (!productionSession) return
  try {
    await productionSession.dispose()
    } catch (err) {
    console.warn('⚠️ ProductionSession dispose エラー:', err)
  }
  productionSession = null
}

async function parseProductionResult(sdkResult) {
  if (!sdkResult) return null
  const speechsdk = await loadSpeechSDK()

  let rawJsonData = null
  try {
    const raw = sdkResult.properties?.getProperty(
      speechsdk.PropertyId.SpeechServiceResponse_JsonResult
    )
    rawJsonData = raw ? (typeof raw === 'string' ? JSON.parse(raw) : raw) : null
  } catch (err) {
    console.warn('⚠️ RAW JSONパースエラー:', err)
  }

  try {
    const pa = speechsdk.PronunciationAssessmentResult.fromResult(sdkResult)
    if (typeof speechEvaluator.parseSDKResult === 'function') {
      return speechEvaluator.parseSDKResult(pa, refText.value, rawJsonData)
    }
    return {
      overall: pa.pronunciationScore ?? null,
      accuracy: pa.accuracyScore ?? null,
      fluency: pa.fluencyScore ?? null,
      completeness: pa.completenessScore ?? null,
      phonemes: []
    }
  } catch (err) {
    console.error('❌ SDK結果の解析に失敗:', err)
    return null
  }
}

function updateProductionClipUrl(blob) {
  if (productionClipUrl.value) {
    URL.revokeObjectURL(productionClipUrl.value)
    productionClipUrl.value = null
  }
  if (blob) {
    productionClipUrl.value = URL.createObjectURL(blob)
  }
}

async function runProductionAttempt(session) {
  const stream = session.getStream()
  if (!stream) {
    throw new Error('マイクストリームを初期化できませんでした')
  }

  const chunks = []
  const mime = pickProductionMime()
  const recorder = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined)
  const stopped = new Promise(resolve => {
    recorder.onstop = resolve
  })
  recorder.ondataavailable = (event) => {
    if (event.data?.size) {
      chunks.push(event.data)
    }
  }
  recorder.start()

  let recognitionResult
  try {
    recognitionResult = await session.recognizeOnce({ silenceMs: PRODUCTION_SILENCE_TIMEOUT })
  } catch (err) {
    recorder.stop()
    await stopped
    if (err?.message === 'SESSION_STOPPED') {
      return { status: 'stopped' }
    }
    throw err
  }

  recorder.stop()
  await stopped

  const blob = chunks.length ? new Blob(chunks, { type: recorder.mimeType || 'audio/webm' }) : null
  updateProductionClipUrl(blob)

  productionAttempts.value += 1

  if (!recognitionResult || recognitionResult.status !== 'success') {
    productionScore.value = null
    productionCurrentMedal.value = null
    return { status: recognitionResult?.status ?? 'silence' }
  }

  const parsed = await parseProductionResult(recognitionResult.result)
  productionScore.value = parsed?.overall ?? null
  productionCurrentMedal.value = determineProductionMedal(productionScore.value)

  if (productionScore.value != null) {
    if (productionBest.value == null || productionScore.value > productionBest.value) {
      productionBest.value = productionScore.value
      productionBestMedal.value = productionCurrentMedal.value ?? productionBestMedal.value
      if (productionScore.value >= TH_GOLD.value) {
        return { status: 'goal', parsed }
      }
    }
  }

  return { status: 'success', parsed }
}

async function runProductionLoop() {
  try {
    const session = await ensureProductionSession()
    productionRunning.value = true
    productionConnecting.value = false
    productionStartAt.value = Date.now()

    while (productionRunning.value) {
      const attemptResult = await runProductionAttempt(session)
      if (!productionRunning.value) break

      if (attemptResult.status === 'goal') {
        break
      }
      if (attemptResult.status === 'stopped') {
        break
      }

      const elapsed = Date.now() - productionStartAt.value
      if (elapsed >= PRODUCTION_DURATION_LIMIT) {
        break
      }

      await sleep(PRODUCTION_INTERVAL_MS)
    }
  } catch (err) {
    if (err?.message !== 'SESSION_STOPPED') {
      console.error('❌ 本番ループエラー:', err)
      alert(err?.message || 'エラーが発生しました')
    }
  } finally {
    productionRunning.value = false
    productionConnecting.value = false
    try {
      if (productionSession) {
        await productionSession.stopRecognition().catch(() => {})
      }
    } catch {}
  }
}

async function startProductionLoop() {
  if (productionConnecting.value || productionRunning.value) return
  if (!refText.value) {
    alert('参照テキストが設定されていません')
      return
    }

  productionConnecting.value = true
  productionRunning.value = false
  productionAttempts.value = 0
  productionScore.value = null
  productionCurrentMedal.value = null
  productionStartAt.value = Date.now()
  productionBest.value = null
  productionBestMedal.value = null

  if (productionClipUrl.value) {
    URL.revokeObjectURL(productionClipUrl.value)
    productionClipUrl.value = null
  }

  productionLoopPromise = runProductionLoop()
    .finally(() => {
      productionLoopPromise = null
    })
    .finally(() => {
      productionConnecting.value = false
    })
}

async function stopProductionLoop() {
  productionConnecting.value = false
  productionRunning.value = false
  if (productionSession) {
    productionSession.cancelActiveAttempt()
    await productionSession.stopRecognition().catch(() => {})
  }
  if (productionLoopPromise) {
    try {
      await productionLoopPromise
    } catch {}
    productionLoopPromise = null
  }
}

async function shareProductionClip() {
  if (!productionCanShare.value || !productionClipUrl.value) return

  try {
    const res = await fetch(productionClipUrl.value)
    const blob = await res.blob()
    const ext = blob.type.includes('webm') ? 'webm' : 'm4a'
    const file = new File([blob], `practice.${ext}`, { type: blob.type || 'audio/mp4' })

    if (navigator.canShare?.({ files: [file] })) {
      await navigator.share({
        files: [file],
        title: '音声評価結果',
        text: `${task.value?.sentence} - スコア: ${productionScore.value}点`
      })
      return
    }

    if (navigator.share) {
      await navigator.share({
        title: '音声評価結果',
        text: `${task.value?.sentence} - スコア: ${productionScore.value}点`,
        url: window.location.href
      })
      return
    }
  } catch (error) {
    console.warn('シェア失敗:', error)
  }

  try {
    const res = await fetch(productionClipUrl.value)
    const blob = await res.blob()
    const ext = blob.type.includes('webm') ? 'webm' : 'm4a'
    const a = document.createElement('a')
    a.href = productionClipUrl.value
    a.download = `practice.${ext}`
    document.body.appendChild(a)
    a.click()
    a.remove()
  } catch (error) {
    alert('このブラウザでは共有できません。ダウンロードをご利用ください。')
  }
}

const handleProductionVisibilityChange = () => {
  if (document.hidden && productionRunning.value) {
    stopProductionLoop()
  }
}

const handleProductionPageHide = () => {
  if (productionRunning.value) {
    stopProductionLoop()
  }
}

// 4) 関数の起動は onMounted 以降に
onMounted(async () => {
  // ページ離脱・バックグラウンド対策のイベントリスナー登録
  document.addEventListener('visibilitychange', handleProductionVisibilityChange)
  window.addEventListener('pagehide', handleProductionPageHide)
  
  if (!taskId.value) {
    console.warn('taskId が未定義。ルーティングパラメータを確認')
    error.value = 'タスクIDが指定されていません'
    isLoading.value = false
    return
  }
  
  // 直リンク対策：taskが無い場合は取得
  // loadTask() が select('*') で全フィールドを取得するため、ここでの select は不要（二重フェッチ回避）
  await loadTask()
  currentIndex.value = taskIndex.value
})

// 5) クエリパラメータの変更に対応
watch(() => route.query.dayId, () => {
  // dayId が更新されたら、必要に応じて再初期化
  if (dayId.value !== null) {
    console.log('dayId変更検知:', dayId.value)
    loadTask()
  }
})

onBeforeUnmount(async () => {
  // イベントリスナーの削除
  document.removeEventListener('visibilitychange', handleProductionVisibilityChange)
  window.removeEventListener('pagehide', handleProductionPageHide)
  
  await stopProductionLoop()
  resetProductionState()
  await disposeProductionSession()
  
  // トークン更新タイマーのクリーンアップ
  await speechEvaluator.cleanup()
})

onUnmounted(() => {
  resetScoreTracking()
})
</script>

<style scoped>
.training-task-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 2rem;
}

.page-header {
  margin-bottom: 2rem;
}

.header-content {
  display: grid;
  grid-template-columns: 1fr auto;
  grid-template-rows: auto auto;
  gap: 0.4rem 1rem;
  background: rgba(255, 255, 255, 0.1);
  padding: 1rem 1.5rem;
  border-radius: 12px;
  backdrop-filter: blur(10px);
}

.header-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 0.4rem;
  grid-column: 1 / 2;
  grid-row: 1 / 3;
  min-width: 0;
}

.header-primary {
  color: white;
  font-size: clamp(1rem, 4vw, 1.4rem);
  font-weight: 700;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  white-space: nowrap;
}

.header-secondary {
  display: flex;
  gap: 0.4rem;
  flex-wrap: nowrap;
  color: rgba(255, 255, 255, 0.95);
  font-weight: 600;
  font-size: clamp(0.65rem, 2.8vw, 0.9rem);
  overflow: hidden;
  justify-content: center;
}

.header-secondary-item {
  background: rgba(255, 255, 255, 0.18);
  border-radius: 999px;
  padding: clamp(0.15rem, 0.6vw, 0.25rem) clamp(0.5rem, 2vw, 0.85rem);
  white-space: nowrap;
  flex-shrink: 1;
  min-width: 0;
  display: flex;
  justify-content: center;
}

.back-button {
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.3);
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  font-weight: bold;
  min-width: 120px;
  text-align: center;
  grid-column: 2 / 3;
  grid-row: 1 / 3;
  align-self: center;
  justify-self: end;
}

.back-button:hover {
  background: rgba(255, 255, 255, 0.3);
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  color: white;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(255, 255, 255, 0.3);
  border-top: 4px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-container {
  text-align: center;
  color: white;
  background: rgba(239, 68, 68, 0.1);
  padding: 2rem;
  border-radius: 12px;
  border: 1px solid rgba(239, 68, 68, 0.3);
}

.retry-button {
  background: #ef4444;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  cursor: pointer;
  margin-top: 1rem;
}

.training-content {
  max-width: 1000px;
  margin: 0 auto;
}

.score-summary {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
}

.score-row {
  display: flex;
  gap: 0.75rem;
  flex-wrap: nowrap;
  justify-content: space-between;
  overflow: hidden;
  padding-bottom: 0.25rem;
}

.score-row.top-row .score-chip {
  flex: 1 1 0;
  min-width: 0;
}

.score-chip.tier-0 { background: rgba(16, 185, 129, 0.25); border-color: rgba(16, 185, 129, 0.5); }
.score-chip.tier-1 { background: rgba(245, 158, 11, 0.25); border-color: rgba(245, 158, 11, 0.5); }
.score-chip.tier-2 { background: rgba(239, 68, 68, 0.25); border-color: rgba(239, 68, 68, 0.5); }

.score-chip,
.score-current,
.score-meta {
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.25);
  padding: 0.6rem clamp(0.5rem, 2vw, 0.9rem);
  color: #fff;
  text-align: center;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.08);
  flex: 0 1 calc(33.33% - 0.5rem);
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.score-chip-label {
  font-size: clamp(0.65rem, 2.2vw, 0.9rem);
  opacity: 0.9;
  margin-bottom: 0.15rem;
  white-space: nowrap;
}

.score-chip-value {
  font-size: clamp(1rem, 3vw, 1.2rem);
  font-weight: 700;
  white-space: nowrap;
}

.score-current {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.4rem;
  margin: 0 auto;
  max-width: 480px;
  width: 100%;
}

.score-current .score-label {
  font-size: clamp(0.75rem, 2.5vw, 0.95rem);
  opacity: 0.85;
  white-space: nowrap;
  text-align: center;
}

.score-current .score-value {
  font-size: clamp(1.1rem, 4vw, 1.5rem);
  font-weight: 700;
  white-space: nowrap;
  text-align: center;
}

.score-row.meta-row {
  justify-content: space-between;
}

.score-meta {
  flex: 0 1 calc(33.33% - 0.5rem);
  min-width: 0;
}

.score-meta-label {
  font-size: clamp(0.7rem, 2.4vw, 0.9rem);
  opacity: 0.85;
  margin-bottom: 0.15rem;
  white-space: nowrap;
}

.score-meta-value {
  font-size: clamp(1rem, 3vw, 1.3rem);
  font-weight: 700;
  white-space: nowrap;
}

.rank-emoji {
  margin-right: 0.35rem;
}

.score-chip.jump,
.score-current.jump,
.score-meta.jump {
  animation: scoreJump 0.4s ease;
}

@keyframes scoreJump {
  0% { transform: scale(1); }
  25% { transform: scale(1.08); }
  100% { transform: scale(1); }
}

.hi-score-banner {
  text-align: center;
  background: rgba(16, 185, 129, 0.18);
  border: 1px solid rgba(16, 185, 129, 0.45);
  color: #d1fae5;
  padding: 0.6rem 1rem;
  border-radius: 999px;
  font-weight: 600;
}

.hi-score-enter-active,
.hi-score-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.hi-score-enter-from,
.hi-score-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

.tts-controls { display: flex; align-items: center; gap: 0.5rem; margin: 0.5rem 0 1rem; }
.tts-label { color: #fff; opacity: 0.9; margin-right: 0.25rem; }
.tts-btn { padding: 0.4rem 0.75rem; border-radius: 6px; border: none; cursor: pointer; }

.completion-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.completion-modal {
  background: white;
  border-radius: 16px;
  padding: 3rem;
  text-align: center;
  max-width: 500px;
  width: 90%;
  box-shadow: 0 20px 25px rgba(0, 0, 0, 0.1);
  animation: modalSlideIn 0.3s ease-out;
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.completion-icon {
  font-size: 4rem;
  margin-bottom: 1rem;
}

.completion-modal h2 {
  color: #1f2937;
  font-size: 2rem;
  margin-bottom: 0.5rem;
}

.completion-modal p {
  color: #6b7280;
  font-size: 1.1rem;
  margin-bottom: 2rem;
}

.completion-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-bottom: 2rem;
}

.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1rem;
  background: #f9fafb;
  border-radius: 8px;
}

.stat-label {
  font-size: 0.9rem;
  color: #6b7280;
  margin-bottom: 0.5rem;
}

.stat-value {
  font-size: 1.5rem;
  font-weight: bold;
  color: #1f2937;
}

.stat-value.excellent {
  color: #10b981;
}

.stat-value.good {
  color: #f59e0b;
}

.stat-value.needs-improvement {
  color: #ef4444;
}

.completion-actions {
  display: flex;
  gap: 1rem;
  justify-content: center;
}

.action-button {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.2s;
}

.action-button.retry {
  background: #f59e0b;
  color: white;
}

.action-button.retry:hover {
  background: #d97706;
}

.action-button.next {
  background: #3b82f6;
  color: white;
}

.action-button.next:hover {
  background: #2563eb;
}

.action-button.back {
  background: #6b7280;
  color: white;
}

.action-button.back:hover {
  background: #4b5563;
}

.task-navigation {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem;
  background: #f9fafb;
  border-radius: 8px;
  margin-top: 1rem;
}

.nav-button {
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  border: none;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.2s;
}

.nav-button.prev {
  background: #e5e7eb;
  color: #1f2937;
}

.nav-button.prev:hover:not(:disabled) {
  background: #d1d5db;
}

.nav-button.next {
  background: #3b82f6;
  color: white;
}

.nav-button.next:hover:not(:disabled) {
  background: #2563eb;
}

.nav-button:disabled {
  background: #9ca3af;
  color: white;
  cursor: not-allowed;
}

.progress-info {
  font-size: 0.9rem;
  color: #6b7280;
  font-weight: bold;
}

@media (max-width: 768px) {
  .training-task-page {
    padding: 1rem;
  }
  
  .header-content {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
  
  .header-info h1 {
    font-size: 1.25rem;
  }
  
  .completion-modal {
    padding: 2rem;
  }
  
  .completion-stats {
    grid-template-columns: 1fr;
  }
  
  .completion-actions {
    flex-direction: column;
  }
}

/* ===== 本番モード（チャレンジ）スタイル ===== */
.challenge-card {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 12px;
  padding: 1.5rem;
  margin-top: 2rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.challenge-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.challenge-header h3 {
  margin: 0;
  color: #1f2937;
  font-size: 1.25rem;
}

.challenge-actions {
  display: flex;
  gap: 0.75rem;
}

.challenge-button {
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  border: none;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.2s;
}

.challenge-button.start {
  background: #3b82f6;
  color: white;
}

.challenge-button.start:hover:not(:disabled) {
  background: #2563eb;
}

.challenge-button.start.connecting {
  background: #6b7280;
  color: #f3f4f6;
  transform: translateY(2px);
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.2);
}

.challenge-button.start.active {
  background: #dc2626;
  color: #fff;
  cursor: default;
}

.challenge-button.stop {
  background: #dc2626;
  color: white;
}

.challenge-button.stop:hover:not(:disabled) {
  background: #b91c1c;
}

.challenge-button:disabled {
  background: #9ca3af;
  color: white;
  cursor: not-allowed;
}

.challenge-status {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
  padding: 1rem;
  background: #f9fafb;
  border-radius: 8px;
}

.challenge-stat {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.stat-label {
  font-size: 0.875rem;
  color: #6b7280;
  margin-bottom: 0.25rem;
}

.stat-value {
  font-size: 1.5rem;
  font-weight: bold;
  color: #1f2937;
}

.stat-value.excellent {
  color: #10b981;
}

.stat-value.good {
  color: #f59e0b;
}

.stat-value.needs-improvement {
  color: #ef4444;
}

.stat-value.badge {
  font-size: 1.25rem;
}

.badge-gold {
  color: #fbbf24;
}

.badge-silver {
  color: #9ca3af;
}

.badge-bronze {
  color: #d97706;
}

.challenge-clip {
  margin-top: 1rem;
  padding: 1rem;
  background: #f9fafb;
  border-radius: 8px;
}

.challenge-clip audio {
  width: 100%;
}

.challenge-share {
  margin-top: 1rem;
  text-align: center;
}

.share-button {
  padding: 0.75rem 1.5rem;
  background: #10b981;
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.2s;
}

.share-button:hover {
  background: #059669;
  transform: scale(1.05);
}
</style>
